import {
  Category,
  Device,
  Indicator,
  InsightsPriorityQuery,
  InsightsPriorityQueryVariables,
  LocationScope,
  Room,
  Site,
  Tenant,
  TimeScope,
  useInsightsPriorityQuery
} from "generated/graphql";
import { CombinedError } from "@urql/core";
import { useSelectedTenants } from "polyrouter";
import {
  getInsightDescriptionTranslation,
  getInsightTitleTranslation
} from "utilities/insights";
import { InsightFilterState } from "./QueryParams";
import { indyTrending } from "collections/InsightDetails";

export type InsightsData = InsightsPriorityQuery["topicIndicators"]["edges"];
type Insight = InsightsData[number];
export type UsePriorityInsight = {
  cursor: string;
  groupBy?: string | null;
  locationScope: LocationScope;
  device: Pick<Device, "name" | "id"> | null;
  deviceType: string | null;
  room: Pick<Room, "name" | "id"> | null;
  tenant: Pick<Tenant, "name" | "id">;
  model: string | null;
  modelVersion: string | null;
  version: string | null;
  family: string | null;
  id: string;
  site: Pick<Site, "name" | "id"> | null;
  period: TimeScope;
  timePeriodStart: string | null;
  title: string;
  higherIsBetter?: boolean;
  details?: string;
  metric: Indicator;
  priorMetric: Indicator | null;
  location?: string;
  trending?: number | undefined;
  category?: Category;
  linkTitle?: string;
};

export const mapInsight = (e: Insight): UsePriorityInsight => ({
  cursor: e.cursor,
  ...e.node,
  period: e.node.timeScope,
  title: getInsightTitleTranslation(e.node.topic.id),
  higherIsBetter: e.node.topic.higherIsBetter,
  details: getInsightDescriptionTranslation(e.node.topic.id),
  metric: e.node.indicator,
  priorMetric: e.node.previous,
  trending: indyTrending(e.node.previous, e.node.indicator),
  category: e.node.topic.category,
  linkTitle: "VIEW_DETAILS"
});

export const responseToInsightData = (data: InsightsData) => {
  return data.map(mapInsight);
};

export const toInput = (
  { date, insight, site, category, locationScope }: InsightFilterState,
  premium?: boolean
) => {
  return {
    locationScope: locationScope ? locationScope : undefined,
    category: premium ? category : Category.Device,
    topic: insight ? [insight] : []
  };
};

export type priorityInsightsListReturnType = {
  total: number;
  insights: Array<UsePriorityInsight>;
  pageInfo: Partial<InsightsPriorityQuery["topicIndicators"]["pageInfo"]>;
};

let usePriorityInsight = (
  filter: InsightFilterState,
  premium: boolean,
  after?: string
): {
  data: priorityInsightsListReturnType;
  fetching: boolean;
  error: CombinedError | undefined;
  stale: boolean;
} => {
  let tenants = useSelectedTenants();
  const variables: InsightsPriorityQueryVariables = {
    tenant: tenants,
    input: toInput(filter, premium),
    after
  };
  const { data, error, stale, fetching } = useInsightsPriorityQuery({
    variables
  });
  let insights: Array<UsePriorityInsight> = [];
  let total = data?.topicIndicators.count ?? 0;
  let pageInfo = {};
  if (data) {
    insights = responseToInsightData(data.topicIndicators.edges);
    pageInfo = data.topicIndicators.pageInfo;
  }
  return {
    data: { pageInfo: pageInfo, total: total, insights: insights },
    error,
    stale,
    fetching
  };
};
export default usePriorityInsight;
